"use client";

import { useEffect, useState } from "react";
import CustomImage from "../CustomImage";
import { AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import { useWindowSize } from "@/hooks/useWindowSize";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper";
import CloseIcon from "@/public/assets/images/X.svg";
import { ImageGroupType } from "@/sections/BaliGallery";
import GalleryGridButtons from "./GalleryGridButtons";
import GalleryModalSlider from "./GalleryModalSlider";

const DynamicModal = dynamic(() => import("../Modal"), { ssr: false });
const DynamicGalleryThumbnailSlider = dynamic(
  () => import("./GalleryThumbnailSlider"),
  { ssr: false }
);

type ImageType = {
  src: {
    desktop: string;
    tablet: string;
    mobile: string;
  };
  alt: string;
};

type Sizes = "one-fourth" | "three-fourth" | "one-third" | "full";

export type GalleryImageType = {
  id: number;
  size: Sizes;
  image: ImageType;
};

type GalleryProps = {
  group: ImageGroupType[];
};

const Gallery = ({ group }: GalleryProps) => {
  const [activeImageId, setActiveImageId] = useState<number | undefined>();
  const [activeImage, setActiveImage] = useState<
    GalleryImageType | undefined
  >();
  const [isNavigationVisible, setIsNavigationVisible] = useState(true);
  const [disablePrev, setDisablePrev] = useState(true);
  const [disableNext, setDisableNext] = useState(false);

  const handleSlideChange = (swiper: SwiperCore) => {
    setDisablePrev(swiper.isBeginning);
    setDisableNext(swiper.isEnd);
  };
  const checkSlidesCount = (swiper: SwiperCore) => {
    const slidesPerView = swiper.params.slidesPerView;
    const enoughSlides = swiper.slides.length > Number(slidesPerView);
    setIsNavigationVisible(enoughSlides);
  };

  const { width } = useWindowSize();

  const images = group.flatMap((grid) => grid.images);

  useEffect(() => {
    if (activeImageId) {
      const image = images.filter((image) => image.id === activeImageId)[0];
      setActiveImage(image);
    } else {
      setActiveImage(undefined);
    }
  }, [activeImageId, images]);

  const renderSizeClass = (size: Sizes) => {
    switch (size) {
      case "one-fourth":
        return "gallery__item--one-fourth";
      case "one-third":
        return "gallery__item--one-third";
      case "three-fourth":
        return "gallery__item--three-fourth";
      case "full":
        return "gallery__item--full";
    }
  };
  return (
    <>
      {!!width ? (
        <>
          {width > 767 ? (
            <Swiper
              spaceBetween={30}
              className={`gallery__swiper-grid ${
                !isNavigationVisible ? "gallery__swiper-grid--no-padding" : ""
              }`.trim()}
              onInit={(swiper) => checkSlidesCount(swiper)}
              onResize={(swiper) => checkSlidesCount(swiper)}
              onSlideChange={(swiper: SwiperCore) => handleSlideChange(swiper)}
            >
              {group.map((grid, index) => (
                <SwiperSlide key={index}>
                  <div className="gallery">
                    {grid.images.map((image) => (
                      <div
                        onClick={() => {
                          setActiveImageId(image.id);
                          document.body.classList.add("no-scroll");
                        }}
                        key={image.id}
                        className={`gallery__item ${renderSizeClass(
                          image.size
                        )}`.trim()}
                      >
                        <CustomImage
                          alt={image.image.alt}
                          src={image.image.src.desktop}
                          srcTablet={image.image.src.tablet}
                          srcMobile={image.image.src.mobile}
                          className="image--bg"
                        />
                      </div>
                    ))}
                  </div>
                </SwiperSlide>
              ))}
              {isNavigationVisible ? (
                <GalleryGridButtons
                  disableNext={disableNext}
                  disablePrev={disablePrev}
                />
              ) : null}
            </Swiper>
          ) : (
            <DynamicGalleryThumbnailSlider
              images={images}
              handleZoom={(image) => {
                setActiveImageId(image.id);
                document.body.classList.add("no-scroll");
              }}
            />
          )}
          <AnimatePresence
            initial={false}
            onExitComplete={() => null}
          ></AnimatePresence>
          {activeImageId && activeImage ? (
            <DynamicModal
              isDarkerBackdrop
              handleClose={() => {
                setActiveImageId(undefined);
                document.body.classList.remove("no-scroll");
              }}
            >
              <div className="gallery__modal">
                <div className="gallery__modal-holder">
                  <button
                    onClick={() => {
                      setActiveImage(undefined);
                      setActiveImageId(undefined);
                      document.body.classList.remove("no-scroll");
                    }}
                    className="gallery__modal-close"
                  >
                    <CloseIcon />
                  </button>
                  <GalleryModalSlider
                    images={images}
                    activeImageId={activeImageId}
                  />
                </div>
              </div>
            </DynamicModal>
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default Gallery;
